/**
 * The worker's own stream — a second SSE drawn onto the SAME board.
 *
 * `peterhof_orch`/`peterhof_tandem` delegate part of a turn to a worker
 * running on its own thread. `worker_started` (a `dh_ui` custom frame on the
 * MAIN stream, `events.ts`) is the signal that one exists; this module opens
 * `resumeRun` onto it, from `-1` — the worker's thread is one this client has
 * never read a byte of — and forwards its `a2ui`/`activity` frames to
 * whatever the caller (`App.tsx`) wires them into. `messages` is deliberately
 * NOT forwarded: those ids belong to the worker's OWN thread, and feeding
 * them into `chatStore` (keyed by the MAIN thread's ids) would draw a second,
 * unrelated transcript into the visitor's conversation — there is nothing to
 * draw from them today, and `forwardableWorkerEvent` says so explicitly
 * rather than by omission.
 *
 * **No `end` event on this endpoint — confirmed live (team-lead's T0
 * experiment on prod, 2026-09-03), not assumed.** `GET
 * /threads/{side}/runs/{run_id}/stream` opens with `metadata`, then `custom`
 * frames (`run_meta`, `activity`, `a2ui`…), and simply CLOSES — EOF, HTTP
 * 200 — after the last one. Exactly the same shape `sse.ts`'s own header
 * measured for the main stream months earlier, so this is the SAME reagent
 * behaviour on a second endpoint, not a new one. `consumeWorkerStream`'s
 * `for await` loop already ends this way with no special casing: the
 * generator returns, the `finally` below fires `onClosed` — pinned by the
 * "clean stream end with no terminal frame at all" test. The `isTerminal`
 * check below (`run_end`/`run_error`) is therefore DEAD CODE on this
 * endpoint today; kept anyway, same reasoning `sse.ts` gives for its own
 * unused `error`-event branch: "a newer reagent may add them, and handling a
 * frame that never comes costs one branch." Status, if ever wanted, is a
 * separate `GET /threads/{side}/runs/{run_id}` call (`status:
 * success|error|interrupted`) — not fetched here, since `onClosed` does not
 * need to distinguish why the connection ended (§module header below).
 *
 * **No resume ladder.** `runClient.ts`'s elaborate one exists because losing
 * the visitor's own answer is expensive; a worker connection that drops is
 * one delegation's progress feed, and the turn's own answer does not depend
 * on this stream staying open (§App.tsx: «не должен ломать Стоп»). So a
 * break — idle past `WORKER_IDLE_MS`, a network failure, or the ordinary EOF
 * above — always just closes the connection. If that turns out to be too
 * little, it is a narrower gap than a copy of the main ladder would have
 * been.
 *
 * **Split into two layers, deliberately mirroring `sse.ts`/`runClient.ts`**:
 * `consumeWorkerStream` drives an already-open `Response`, which is what a
 * test can build by hand (`sse.test.ts`'s own idle-watchdog fixtures do
 * exactly this); `startWorkerStream` is the thin `fetch`-calling wrapper,
 * which — like `runClient.ts` itself — has no unit test in this repo.
 */

import { resumeRun } from './api';
import { normalizeFrame, type NormalizedEvent } from './events';
import { readSse } from './sse';
import { create } from 'zustand';

const PERSIST_PREFIX = 'ph.worker.';

/**
 * The platform's event-log broker lives an hour (`api.ts`'s own `resumeRun`
 * doc), but nothing here promises the worker's stream stays open that long —
 * 5 minutes of silence closes it from OUR side, rather than holding a
 * connection open for up to an hour on the chance the worker is still going.
 */
export const WORKER_IDLE_MS = 5 * 60 * 1000;

export type WorkerStreamIdentity = { sideThreadId: string; runId: string };
type Persisted = WorkerStreamIdentity & { lastEventId: string | null };

/** Best-effort read; a session that cannot read back what it wrote just does
 *  not reconnect after F5 — the same posture `activityStore.ts`/
 *  `boardPanelStore.ts` take on `localStorage`. `sessionStorage` rather than
 *  `localStorage`: the side stream is a fact about THIS tab's live turn, not
 *  something a different tab or a future visit should inherit. */
function readSessionStorage(mainThreadId: string): Persisted | null {
  try {
    const raw = window.sessionStorage.getItem(PERSIST_PREFIX + mainThreadId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Persisted> | null;
    if (!parsed || typeof parsed.sideThreadId !== 'string' || typeof parsed.runId !== 'string') return null;
    return {
      sideThreadId: parsed.sideThreadId,
      runId: parsed.runId,
      lastEventId: typeof parsed.lastEventId === 'string' ? parsed.lastEventId : null,
    };
  } catch {
    return null;
  }
}

function writeSessionStorage(mainThreadId: string, value: Persisted | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(PERSIST_PREFIX + mainThreadId);
    else window.sessionStorage.setItem(PERSIST_PREFIX + mainThreadId, JSON.stringify(value));
  } catch {
    /* a session that cannot persist just does not come back after F5 */
  }
}

/**
 * Should this event reach the caller at all?
 *
 * The worker's stream carries the same envelope shapes the main turn's does
 * — it is one more `reagent` run — but only `a2ui` and `activity` are ones
 * this client draws anything from for a SIDE stream (§module header). A
 * `reasoning` frame from the worker is deliberately excluded too: whether a
 * worker's own thinking belongs on the visitor's reasoning block is a product
 * question this module does not answer on its own — see the coordinator's
 * report for it as an open contract question, not a silent choice.
 */
export function forwardableWorkerEvent(event: NormalizedEvent): boolean {
  // `worker_started` too (2026-09-15, `peterhof_tandem`'s queue): a worker that
  // finishes starts the conversation's queued job and announces it on ITS OWN
  // stream — no main turn carries that frame, so this is the only place the
  // client can learn the next run exists.
  //
  // `progress`/`worker_done` (2026-09-16, `peterhof_live`): the helper runs
  // ENTIRELY on the worker's own thread — there is no in-turn message on the
  // main stream to carry the narrator's lines or the finish signal, unlike
  // `peterhof_orch`'s inline helper — so both frames arrive here and nowhere
  // else for the ordinary case. The wire contract also allows either frame on
  // the MAIN stream (defensive completeness on the backend's part), which is
  // why `App.tsx`'s own `onEvent` handles them too; this function only says
  // what the SIDE stream may forward.
  return (
    event.kind === 'a2ui' ||
    event.kind === 'activity' ||
    event.kind === 'worker_started' ||
    event.kind === 'progress' ||
    event.kind === 'worker_done'
  );
}

/** Is this frame the end of the worker's own run? Neither `run_end` nor
 *  `run_error` is ever sent on this endpoint (§module header, confirmed
 *  live) — the stream closes by EOF instead, which `consumeWorkerStream`'s
 *  `for await` loop already handles with no help from this function. Kept
 *  for a reagent that DOES start sending one: neither would be forwarded —
 *  the worker's `run_error` is not the main turn's error (§App.tsx: a second
 *  stream may not fail the visitor's turn) — but either would mean "stop
 *  reading", same as EOF does today. */
function isTerminal(event: NormalizedEvent): boolean {
  return event.kind === 'run_end' || event.kind === 'run_error';
}

export type WorkerStreamHandlers = {
  onEvent: (event: NormalizedEvent) => void;
  /** Fired once the connection is done being useful — an ordinary EOF close
   *  (the common case, confirmed live: §module header), an idle timeout, a
   *  terminal frame if one ever arrives, or the fetch/read failing outright.
   *  Never fired for a `stop()` the caller itself initiated; that caller
   *  already knows. */
  onClosed?: () => void;
};

/**
 * Drives one already-open worker `Response` to completion.
 *
 * Persists `lastEventId` after every frame carrying one — kept for an
 * accurate on-disk record even though `reconnect()` below always replays
 * from `-1` rather than resuming from it (§d of the coordinator's spec: the
 * worker's cards are not in `/state` yet, so a partial resume would leave
 * everything before the stored id undrawn).
 */
export async function consumeWorkerStream(
  response: Response,
  mainThreadId: string,
  identity: WorkerStreamIdentity,
  handlers: WorkerStreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  try {
    for await (const frame of readSse(response, { signal, idleMs: WORKER_IDLE_MS })) {
      if (signal.aborted) return; // stop()/a newer start() already moved on
      if (frame.id) writeSessionStorage(mainThreadId, { ...identity, lastEventId: frame.id });
      const event = normalizeFrame(frame);
      if (event.kind === 'ignored') continue;
      if (isTerminal(event)) return; // finally{} below reports the close
      if (forwardableWorkerEvent(event)) handlers.onEvent(event);
    }
  } catch {
    // An idle break (`SseIdleError`, past `WORKER_IDLE_MS`) or the read
    // failing outright — no resume ladder here (§module header), so either
    // one just ends the read; `finally` below reports it the same way a
    // clean close does. A `stop()`-triggered `AbortError` also lands here
    // and is told apart from the two above by `signal.aborted`, same as a
    // clean close is.
  } finally {
    if (!signal.aborted) handlers.onClosed?.();
  }
}

/** The `fetch`-calling wrapper — see the module header for why it is split
 *  from `consumeWorkerStream` and carries no unit test of its own. */
export async function startWorkerStream(
  mainThreadId: string,
  identity: WorkerStreamIdentity,
  lastEventId: string | null,
  handlers: WorkerStreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  writeSessionStorage(mainThreadId, { ...identity, lastEventId });
  let response: Response;
  try {
    response = await resumeRun(identity.sideThreadId, identity.runId, lastEventId, signal);
  } catch {
    if (!signal.aborted) handlers.onClosed?.();
    return;
  }
  await consumeWorkerStream(response, mainThreadId, identity, handlers, signal);
}

export type WorkerStreamState = {
  /** Is a side stream currently open? The frames themselves go straight to
   *  whatever the caller's `onEvent` feeds (the board, the activity feed) —
   *  this flag exists so `worker_started` arriving twice in one turn closes
   *  the first connection instead of leaking it. */
  active: boolean;
  identity: WorkerStreamIdentity | null;

  /** `worker_started` on the main stream: opens the side connection, always
   *  replaying from the start. Closes any PREVIOUS side connection first — a
   *  later delegation in the same conversation is a fresh worker, not a
   *  continuation of the last one. */
  start: (mainThreadId: string, identity: WorkerStreamIdentity, onEvent: (event: NormalizedEvent) => void) => void;
  /** F5: reconnects to whatever `start()` last persisted for `mainThreadId`,
   *  replaying from `-1` (§d — the worker's cards are not part of `/state`
   *  yet). A no-op when nothing was persisted for this thread. */
  reconnect: (mainThreadId: string, onEvent: (event: NormalizedEvent) => void) => void;
  /** Aborts the side connection without touching the main turn — «Стоп»
   *  never calls this, only a new thread does (§App.tsx). */
  stop: () => void;
  reset: () => void;
};

/** Non-reactive connection handle — an `AbortController` has no business
 *  being store state, the same reasoning `App.tsx`'s own `abort = useRef`
 *  follows for the main turn. A generation counter guards against a stale
 *  connection's `onClosed` firing after a NEWER `start()`/`stop()` already
 *  moved the store on. */
let controller: AbortController | null = null;
let generation = 0;

function closeController(): void {
  controller?.abort();
  controller = null;
}

export const useWorkerStream = create<WorkerStreamState>((set) => ({
  active: false,
  identity: null,

  start: (mainThreadId, identity, onEvent) => {
    closeController();
    generation += 1;
    const myGen = generation;
    const ac = new AbortController();
    controller = ac;
    set({ active: true, identity });
    void startWorkerStream(
      mainThreadId,
      identity,
      null,
      {
        onEvent,
        onClosed: () => {
          if (myGen !== generation) return; // superseded — a later call already reset this
          controller = null;
          set({ active: false, identity: null });
        },
      },
      ac.signal,
    );
  },

  reconnect: (mainThreadId, onEvent) => {
    const persisted = readSessionStorage(mainThreadId);
    if (!persisted) return;
    closeController();
    generation += 1;
    const myGen = generation;
    const identity: WorkerStreamIdentity = { sideThreadId: persisted.sideThreadId, runId: persisted.runId };
    const ac = new AbortController();
    controller = ac;
    set({ active: true, identity });
    void startWorkerStream(
      mainThreadId,
      identity,
      '-1',
      {
        onEvent,
        onClosed: () => {
          if (myGen !== generation) return;
          controller = null;
          set({ active: false, identity: null });
        },
      },
      ac.signal,
    );
  },

  stop: () => {
    closeController();
    generation += 1;
    set({ active: false, identity: null });
  },

  reset: () => {
    closeController();
    generation += 1;
    set({ active: false, identity: null });
  },
}));
